import { supabase } from '../supabase';

/**
 * Handles Supabase errors, particularly JWT expiration
 * @param {Error} error - The error object from Supabase
 * @param {Function} onSessionExpired - Callback function when session expires
 * @returns {string} - User-friendly error message
 */
export const handleSupabaseError = async (error, onSessionExpired) => {
  console.error('Supabase error:', error);

  // Check for JWT expiration
  if (error?.code === 'PGRST301' || error?.message?.includes('JWT expired')) {
    console.log('JWT expired, attempting to refresh session...');
    
    // Try to refresh the session
    const { data: { session }, error: refreshError } = await supabase.auth.refreshSession();
    
    if (refreshError || !session) {
      console.error('Session refresh failed:', refreshError);
      
      // Sign out and redirect to login
      await supabase.auth.signOut();
      
      if (onSessionExpired) {
        onSessionExpired();
      }
      
      return 'Votre session a expiré. Veuillez vous reconnecter.';
    }
    
    console.log('Session refreshed successfully');
    return 'Session rafraîchie. Veuillez réessayer.';
  }

  // Handle other common errors
  if (error?.code === '23505') {
    return 'Cette entrée existe déjà.';
  }
  
  if (error?.code === '23503') {
    return 'Impossible de supprimer: cette entrée est référencée ailleurs.';
  }
  
  if (error?.code === '42501') {
    return 'Vous n\'avez pas la permission d\'effectuer cette action.';
  }

  // Generic error message
  return error?.message || 'Une erreur est survenue. Veuillez réessayer.';
};

/**
 * Wraps a Supabase query with automatic error handling
 * @param {Function} queryFn - The async function that performs the Supabase query
 * @param {Function} onSessionExpired - Optional callback for session expiration
 * @returns {Promise} - The result of the query or handled error
 */
export const withErrorHandling = async (queryFn, onSessionExpired) => {
  try {
    const result = await queryFn();
    
    if (result.error) {
      const errorMessage = await handleSupabaseError(result.error, onSessionExpired);
      throw new Error(errorMessage);
    }
    
    return result;
  } catch (error) {
    if (error?.code === 'PGRST301' || error?.message?.includes('JWT expired')) {
      const errorMessage = await handleSupabaseError(error, onSessionExpired);
      throw new Error(errorMessage);
    }
    throw error;
  }
};

/**
 * Wraps a Supabase mutation (insert/update/delete) with automatic session refresh
 * and a single transparent retry. If the token was expired, it refreshes silently
 * and re-runs the operation so the user never sees an error.
 *
 * Usage:
 *   const result = await withRetry(() =>
 *     supabase.from('trips').insert([...]).select()
 *   );
 *   if (result.error) throw result.error;
 *
 * @param {Function} queryFn - Zero-argument async function that returns a Supabase result
 * @param {Function} [onSessionExpired] - Called when refresh fails and the user must log in again
 * @returns {Promise} - The Supabase result object
 */
export const withRetry = async (queryFn, onSessionExpired) => {
  const isJwtError = (err) =>
    err?.code === 'PGRST301' ||
    err?.status === 401 ||
    err?.message?.toLowerCase().includes('jwt expired') ||
    err?.message?.toLowerCase().includes('invalid jwt');

  const attemptRefresh = async () => {
    const { data: { session }, error: refreshError } = await supabase.auth.refreshSession();
    if (refreshError || !session) {
      console.error('Session refresh failed, signing out:', refreshError);
      await supabase.auth.signOut();
      if (onSessionExpired) onSessionExpired();
      throw new Error('Votre session a expiré. Veuillez vous reconnecter.');
    }
    console.log('Session refreshed, retrying operation...');
    return session;
  };

  let result;
  try {
    result = await queryFn();
  } catch (err) {
    if (isJwtError(err)) {
      await attemptRefresh();
      result = await queryFn();
    } else {
      throw err;
    }
  }

  // Supabase returns errors inside the result object (not thrown)
  if (result?.error && isJwtError(result.error)) {
    await attemptRefresh();
    result = await queryFn();
  }

  return result;
};
